/**
 * Cross-tab session coordination using BroadcastChannel API.
 * WP-AUTH-12: httpOnly Cookie for Refresh Token Persistence
 * WP-SESSION-3: Activity Tracking & Auto-Logout Warning
 *
 * Used for:
 * 1. Logout propagation - when user logs out in one tab, all tabs should redirect
 * 2. Token rotation sync - when refresh token rotates, other tabs need to know
 * 3. Activity sync - when user is active in one tab, all tabs should know (WP-SESSION-3)
 * 4. Extend session - when user extends session in one tab, all tabs should dismiss warning (WP-SESSION-3)
 *
 * Safari < 15.4 doesn't support BroadcastChannel, so we fall back to localStorage events.
 */

type SessionEvent =
  | { type: 'LOGOUT' }
  | { type: 'TOKEN_ROTATED' }
  | { type: 'ACTIVITY'; timestamp: number }
  | { type: 'EXTEND_SESSION' }
  | { type: 'TOKEN_REFRESHED'; accessToken: string };

const CHANNEL_NAME = 'flowio_session';
const LOGOUT_STORAGE_KEY = 'flowio_logout_event';
const ACTIVITY_STORAGE_KEY = 'flowio_activity_event';
const EXTEND_SESSION_STORAGE_KEY = 'flowio_extend_session_event';

let channel: BroadcastChannel | null = null;
let logoutHandler: (() => void) | null = null;
let activityHandler: ((timestamp: number) => void) | null = null;
let extendSessionHandler: (() => void) | null = null;

/**
 * Initialize the session channel for cross-tab communication.
 * Call this once on app mount (in AuthProvider).
 *
 * @param onLogout - Callback to execute when another tab broadcasts logout
 */
export function initSessionChannel(onLogout: () => void): void {
  // Store the handler for later use
  logoutHandler = onLogout;

  if (typeof BroadcastChannel === 'undefined') {
    // Safari < 15.4 doesn't support BroadcastChannel
    // Fallback: use localStorage events (less reliable but works)
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', handleStorageEvent);
    }
    return;
  }

  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (event: MessageEvent<SessionEvent>) => {
    if (event.data.type === 'LOGOUT') {
      onLogout();
    } else if (event.data.type === 'ACTIVITY' && activityHandler) {
      activityHandler(event.data.timestamp);
    } else if (event.data.type === 'EXTEND_SESSION' && extendSessionHandler) {
      extendSessionHandler();
    }
  };
}

/**
 * Handle storage events for Safari fallback.
 * This listens for logout, activity, and extend session events triggered by other tabs.
 */
function handleStorageEvent(e: StorageEvent): void {
  if (e.key === LOGOUT_STORAGE_KEY && logoutHandler) {
    logoutHandler();
  } else if (e.key === ACTIVITY_STORAGE_KEY && activityHandler && e.newValue) {
    activityHandler(parseInt(e.newValue, 10));
  } else if (e.key === EXTEND_SESSION_STORAGE_KEY && extendSessionHandler) {
    extendSessionHandler();
  }
}

/**
 * Broadcast a logout event to all other tabs.
 * Call this after successful logout.
 */
export function broadcastLogout(): void {
  if (channel) {
    channel.postMessage({ type: 'LOGOUT' });
  } else if (typeof localStorage !== 'undefined') {
    // Safari fallback: set and immediately remove a localStorage item
    // This triggers a 'storage' event in other tabs
    localStorage.setItem(LOGOUT_STORAGE_KEY, Date.now().toString());
    localStorage.removeItem(LOGOUT_STORAGE_KEY);
  }
}

/**
 * Broadcast a token rotation event to all other tabs.
 * Note: Not currently used but available for future token sync needs.
 */
export function broadcastTokenRotation(): void {
  if (channel) {
    channel.postMessage({ type: 'TOKEN_ROTATED' });
  }
}

/**
 * Broadcast user activity to all other tabs.
 * WP-SESSION-3: Activity Tracking & Auto-Logout Warning
 *
 * @param timestamp - The timestamp of the activity (Date.now() value)
 */
export function broadcastActivity(timestamp: number): void {
  if (channel) {
    channel.postMessage({ type: 'ACTIVITY', timestamp });
  } else if (typeof localStorage !== 'undefined') {
    // Safari fallback
    localStorage.setItem(ACTIVITY_STORAGE_KEY, timestamp.toString());
    localStorage.removeItem(ACTIVITY_STORAGE_KEY);
  }
}

/**
 * Broadcast extend session event to all other tabs.
 * WP-SESSION-3: Activity Tracking & Auto-Logout Warning
 *
 * Called when user clicks "Stay logged in" in the warning modal.
 * All tabs should dismiss their warning and reset their timers.
 */
export function broadcastExtendSession(): void {
  if (channel) {
    channel.postMessage({ type: 'EXTEND_SESSION' });
  } else if (typeof localStorage !== 'undefined') {
    // Safari fallback
    localStorage.setItem(EXTEND_SESSION_STORAGE_KEY, Date.now().toString());
    localStorage.removeItem(EXTEND_SESSION_STORAGE_KEY);
  }
}

/**
 * Register a callback to receive activity events from other tabs.
 * WP-SESSION-3: Activity Tracking & Auto-Logout Warning
 *
 * @param handler - Callback to execute when activity is received
 * @returns Cleanup function to unregister the handler
 */
export function onActivityReceived(handler: (timestamp: number) => void): () => void {
  activityHandler = handler;
  return () => {
    activityHandler = null;
  };
}

/**
 * Register a callback to receive extend session events from other tabs.
 * WP-SESSION-3: Activity Tracking & Auto-Logout Warning
 *
 * @param handler - Callback to execute when extend session is received
 * @returns Cleanup function to unregister the handler
 */
export function onExtendSessionReceived(handler: () => void): () => void {
  extendSessionHandler = handler;
  return () => {
    extendSessionHandler = null;
  };
}

/**
 * Close the session channel and clean up listeners.
 * Call this on app unmount if needed.
 */
export function closeSessionChannel(): void {
  if (channel) {
    channel.close();
    channel = null;
  }

  if (typeof window !== 'undefined') {
    window.removeEventListener('storage', handleStorageEvent);
  }

  logoutHandler = null;
  activityHandler = null;
  extendSessionHandler = null;
}

// ============================================
// WP-AUTH-REFRESH-DEDUP-1: Layer 2 - Cross-Tab Refresh Coordination
// ============================================

interface TokenRefreshLock {
  lockId: string;
  tabId: string;
  acquiredAt: number;
  expiresAt: number;
}

interface TokenRefreshResult {
  accessToken: string;
  timestamp: number;
}

const REFRESH_LOCK_KEY = 'flowio_refresh_lock';
const REFRESH_RESULT_KEY = 'flowio_refresh_result';
const LOCK_TIMEOUT_MS = 10000; // 10 seconds
const RESULT_VALIDITY_MS = 5000; // 5 seconds

const tabId = typeof crypto !== 'undefined' ? crypto.randomUUID() : Math.random().toString(36);

/**
 * Attempt to acquire cross-tab refresh lock.
 * @returns Lock ID if acquired, null if another tab holds the lock
 */
export function tryAcquireRefreshLock(): string | null {
  const now = Date.now();
  const existingLock = localStorage.getItem(REFRESH_LOCK_KEY);

  if (existingLock) {
    try {
      const lock: TokenRefreshLock = JSON.parse(existingLock);
      if (lock.expiresAt > now) {
        console.debug(`[CrossTab] Lock held by tab ${lock.tabId}, waiting...`);
        return null;
      }
      console.debug(`[CrossTab] Taking over expired lock from tab ${lock.tabId}`);
    } catch {
      // Invalid lock data, proceed to acquire
    }
  }

  const lockId = typeof crypto !== 'undefined' ? crypto.randomUUID() : Math.random().toString(36);
  const lock: TokenRefreshLock = {
    lockId,
    tabId,
    acquiredAt: now,
    expiresAt: now + LOCK_TIMEOUT_MS,
  };

  localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(lock));

  // Double-check we got the lock (handle race condition)
  const verifyLock = localStorage.getItem(REFRESH_LOCK_KEY);
  if (verifyLock) {
    try {
      const verified: TokenRefreshLock = JSON.parse(verifyLock);
      if (verified.lockId === lockId) {
        console.debug(`[CrossTab] Lock acquired: ${lockId}`);
        return lockId;
      }
    } catch {
      // Ignore parse errors
    }
  }

  console.debug('[CrossTab] Lock race lost');
  return null;
}

/**
 * Release the refresh lock.
 */
export function releaseRefreshLock(lockId: string): void {
  const existingLock = localStorage.getItem(REFRESH_LOCK_KEY);
  if (existingLock) {
    try {
      const lock: TokenRefreshLock = JSON.parse(existingLock);
      if (lock.lockId === lockId) {
        localStorage.removeItem(REFRESH_LOCK_KEY);
        console.debug(`[CrossTab] Lock released: ${lockId}`);
      }
    } catch {
      // Ignore parse errors
    }
  }
}

/**
 * Publish refresh result to other tabs.
 */
export function publishRefreshResult(accessToken: string): void {
  const result: TokenRefreshResult = {
    accessToken,
    timestamp: Date.now(),
  };
  localStorage.setItem(REFRESH_RESULT_KEY, JSON.stringify(result));

  // Also broadcast via channel for faster propagation
  if (channel) {
    channel.postMessage({ type: 'TOKEN_REFRESHED', accessToken });
  }

  console.debug('[CrossTab] Refresh result published');
}

/**
 * Wait for refresh result from another tab.
 * @param timeoutMs Maximum time to wait
 * @returns Access token if received, null if timeout
 */
export async function waitForRefreshResult(timeoutMs: number = LOCK_TIMEOUT_MS): Promise<string | null> {
  const startTime = Date.now();

  return new Promise((resolve) => {
    // Check for existing recent result
    const existingResult = localStorage.getItem(REFRESH_RESULT_KEY);
    if (existingResult) {
      try {
        const result: TokenRefreshResult = JSON.parse(existingResult);
        if (Date.now() - result.timestamp < RESULT_VALIDITY_MS) {
          console.debug('[CrossTab] Using existing refresh result');
          resolve(result.accessToken);
          return;
        }
      } catch {
        // Ignore parse errors
      }
    }

    let messageHandler: ((event: MessageEvent) => void) | null = null;

    // Listen for broadcast
    if (channel) {
      messageHandler = (event: MessageEvent) => {
        if (event.data?.type === 'TOKEN_REFRESHED') {
          console.debug('[CrossTab] Received refresh result via broadcast');
          resolve(event.data.accessToken);
          cleanup();
        }
      };
      channel.addEventListener('message', messageHandler);
    }

    // Poll localStorage as fallback
    const pollInterval = setInterval(() => {
      const result = localStorage.getItem(REFRESH_RESULT_KEY);
      if (result) {
        try {
          const parsed: TokenRefreshResult = JSON.parse(result);
          if (parsed.timestamp > startTime) {
            console.debug('[CrossTab] Received refresh result via localStorage');
            resolve(parsed.accessToken);
            cleanup();
          }
        } catch {
          // Ignore parse errors
        }
      }

      if (Date.now() - startTime > timeoutMs) {
        console.debug('[CrossTab] Timeout waiting for refresh result');
        resolve(null);
        cleanup();
      }
    }, 100);

    const cleanup = () => {
      clearInterval(pollInterval);
      if (channel && messageHandler) {
        channel.removeEventListener('message', messageHandler);
      }
    };
  });
}
